// client/src/hooks/useStructureInfo.js
// Итерация 6 + 8.6: загрузка данных структуры через Gateway.
//
// Что изменилось в 8.6:
//   - Локальный useRef-кэш заменён на глобальный structureCache.
//   - Защита от гонок — глобальная (inFlight в structureCache).
//   - Хук — тонкая обёртка над loadStructure + subscribe.
//
// Контракт НЕ изменился: useStructureInfo(structureId, structureType) → {
//   structureInfo, loading, error, refetch
// }

import { useState, useEffect, useCallback, useRef } from 'react';
import Gateway from '../api/gateway';
import {
  buildKey,
  getCached,
  loadStructure,
  subscribe,
} from '../utils/structureCache';

// Маппинг: locationInfo.type → метод Gateway
const FETCHER_BY_TYPE = {
  station: (id) => Gateway.api.spaceStation.getStation(id),
  space_station: (id) => Gateway.api.spaceStation.getStation(id),
  ship: (id) => Gateway.api.ship.getShip(id),
  shipyard: (id) => Gateway.api.shipyard.getShipyard(id),
  spaceport: (id) => Gateway.api.spaceport.getSpaceport(id),
  civil_structure: (id) => Gateway.api.civilStructure.getStructure(id),
  structure: (id) => Gateway.api.civilStructure.getStructure(id),
  military_structure: (id) => Gateway.api.militaryStructure.getMilitaryStructure(id),
};

function resolveFetcher(structureType) {
  if (!structureType) return null;
  return FETCHER_BY_TYPE[String(structureType).toLowerCase()] || null;
}

export default function useStructureInfo(structureId, structureType) {
  const [structureInfo, setStructureInfo] = useState(() => {
    // Начальное состояние — из глобального кэша (если есть)
    const key = buildKey(structureType, structureId);
    return key ? getCached(key) : null;
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const isMountedRef = useRef(true);
  const lastKeyRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchStructure = useCallback(
    async (force = false) => {
      const fetcher = resolveFetcher(structureType);
      if (!structureId || !fetcher) {
        setStructureInfo(null);
        setError(null);
        return null;
      }

      const key = buildKey(structureType, structureId);
      lastKeyRef.current = key;

      // Мгновенный кэш hit (без loading)
      if (!force) {
        const cached = getCached(key);
        if (cached) {
          setStructureInfo(cached);
          setError(null);
          return cached;
        }
      }

      setLoading(true);
      setError(null);

      try {
        const data = await loadStructure(
          structureType,
          structureId,
          () => fetcher(structureId),
          force
        );

        if (!isMountedRef.current) return data;
        if (lastKeyRef.current !== key) return data; // гонка (id сменился)

        if (data) {
          setStructureInfo(data);
        } else {
          setStructureInfo(null);
          setError('Failed to load structure');
        }
        return data;
      } catch (err) {
        if (!isMountedRef.current) return null;
        setError(err?.message || 'Failed to load structure');
        setStructureInfo(null);
        return null;
      } finally {
        if (isMountedRef.current) setLoading(false);
      }
    },
    [structureId, structureType]
  );

  // Подписка на глобальный кэш + начальная загрузка
  useEffect(() => {
    if (!structureId || !structureType) {
      setStructureInfo(null);
      setError(null);
      return undefined;
    }

    const key = buildKey(structureType, structureId);
    lastKeyRef.current = key;

    // Подписка: если кто-то другой загрузит — обновимся
    const unsubscribe = subscribe(key, (data) => {
      if (!isMountedRef.current) return;
      if (lastKeyRef.current !== key) return;
      setStructureInfo(data || null);
      if (data) setError(null);
    });

    // Триггер загрузки
    fetchStructure(false);

    return () => {
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structureId, structureType]);

  return {
    structureInfo,
    loading,
    error,
    refetch: () => fetchStructure(true),
  };
}
